import { useState } from 'react';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useContractorFieldsConfig from './useContractorFieldsConfig';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../../hooks/useTranslate';
import useContractors from './useContractors';
import useAlerts from './useAlerts';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';
import { getItemFromId } from '../../WorkersAndMedicalExaminations/helpers';
import { CONTRACTOR_BUSINESS_TYPES } from '../consts';

export interface IContractorForm {
    id: string;
    cuit: string;
    companyActivity: string;
    companyType: any;
    employeeQuantity: string;
}
export const initialContractorForm: IContractorForm = {
    id: '',
    cuit: '',
    companyActivity: '',
    companyType: '',
    employeeQuantity: ''
};
export interface ContractorFormProps {
    selectedContractor?: IContractorForm;
    isEditing: boolean;
    handleCloseDrawer: any;
}
interface GetInitialData {
    selectedContractor: any;
    isEditing: boolean;
}
const getInitialData = ({ selectedContractor, isEditing }: GetInitialData) => {
    if (!isEditing) return initialContractorForm;
    return {
        id: selectedContractor?.id || '',
        cuit: selectedContractor?.cuit || '',
        companyActivity: selectedContractor?.idActividad || '',
        companyType:
            (selectedContractor?.tipoEmpresa &&
                getItemFromId(selectedContractor.tipoEmpresa, CONTRACTOR_BUSINESS_TYPES)) ||
            '',
        employeeQuantity: selectedContractor?.cantidadTrabajadores || ''
    };
};
export const useContractorForm = ({
    selectedContractor,
    isEditing,
    handleCloseDrawer
}: ContractorFormProps) => {
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const [formData, setFormData] = useState<IContractorForm>(
        getInitialData({ selectedContractor, isEditing })
    );
    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.responsiblesAndContractors'
    });

    const { addContractor, editContractor, isAddingContractor, isEditingContractor } =
        useContractors();
    const { showConfirmCreateContractorAlert, showConfirmEditContractorAlert } = useAlerts({
        translate: t
    });

    const { translate } = useTranslate(t);

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { contractorFieldsConfig } = useContractorFieldsConfig({
        t,
        formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleCreateNewContractor = async () => {
        try {
            if (!selectedCarcinogenicReportId) throw new Error('No report selected');

            const adaptedBody = {
                cuit: formData.cuit,
                idActividad: Number(formData.companyActivity),
                tipoEmpresa: formData.companyType,
                cantidadTrabajadores: Number(formData.employeeQuantity)
            };
            const response: any = await addContractor({ reportId: selectedCarcinogenicReportId, body: adaptedBody });

            if (response?.error) {
                enqueueSnackbar(translate('enqueues.createContractor.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.createContractor.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleEditContractor = async () => {
        try {

            if (!selectedCarcinogenicReportId || !formData?.id)
                throw new Error('No report selected');

            const adaptedBody = {
                cuit: formData.cuit,
                idActividad: Number(formData.companyActivity),
                tipoEmpresa: formData.companyType?.id,
                cantidadTrabajadores: Number(formData.employeeQuantity)
            };
            const response: any = await editContractor({
                reportId: selectedCarcinogenicReportId,
                contractorId: Number(formData.id),
                body: adaptedBody
            });

            if (response?.error) {
                enqueueSnackbar(translate('enqueues.editContractor.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.editContractor.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
            
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleSubmit = () => {
        if (!validateForm(formData, contractorFieldsConfig)) return;
        if (isEditing) {
            showConfirmEditContractorAlert({ confirmAction: handleEditContractor });
        } else {
            showConfirmCreateContractorAlert({ confirmAction: handleCreateNewContractor });
        }
    };

    const resetForm = () => {
        setFormData(initialContractorForm);
    };

    return {
        formData,
        errors,
        contractorFieldsConfig,
        isAddingContractor,
        isEditingContractor,
        translate,
        resetForm,
        handleChange,
        validateForm,
        handleSubmit
    };
};
